<template>
  <AdminLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-2xl font-heading font-bold text-mifo-primary">
            Kurse
          </h1>
          <p class="mt-1 text-sm text-gray-500">
            Verwalten Sie alle Kurse und deren Verfügbarkeit
          </p>
        </div>
        <button
          @click="showCreateModal = true"
          class="btn-primary"
        >
          <Plus class="h-5 w-5 mr-2 inline-block align-middle" />
          Neuer Kurs
        </button>
      </div>

      <!-- Course Table -->
      <CourseTable
        :courses="courses"
        :show-filters="true"
        :show-sorting="true"
        :show-end-date="true"
        :show-id="true"
        @course-click="viewCourseDetails"
      />
    </div>

    <!-- Create/Edit Course Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="showCreateModal = false"
    >
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <div class="mt-3">
          <h3 class="text-lg font-heading font-medium text-mifo-primary mb-4">
            {{ editingCourse ? 'Kurs bearbeiten' : 'Neuer Kurs' }}
          </h3>
          
          <form @submit.prevent="saveCourse">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Kurs ID
                </label>
                <input
                  v-model="courseForm.id"
                  type="text"
                  required
                  class="input-field"
                  placeholder="z.B. ECHO-001"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Titel
                </label>
                <input
                  v-model="courseForm.title"
                  type="text"
                  required
                  class="input-field"
                  placeholder="Kurstitel"
                />
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Startdatum
                  </label>
                  <input
                    v-model="courseForm.startDate"
                    type="date"
                    required
                    class="input-field"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Enddatum
                  </label>
                  <input
                    v-model="courseForm.endDate"
                    type="date"
                    required
                    class="input-field"
                  />
                </div>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Stadt
                </label>
                <input
                  v-model="courseForm.city"
                  type="text"
                  required
                  class="input-field"
                  placeholder="Stadt"
                />
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Gesamtplätze
                  </label>
                  <input
                    v-model.number="courseForm.totalSpots"
                    type="number"
                    required
                    min="1"
                    class="input-field"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Verkaufte Plätze
                  </label>
                  <input
                    v-model.number="courseForm.soldSpots"
                    type="number"
                    required
                    min="0"
                    :max="courseForm.totalSpots"
                    class="input-field"
                  />
                </div>
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Standard Preis (in Cent)
                  </label>
                  <input
                    v-model.number="courseForm.prices.standard"
                    type="number"
                    required
                    min="0"
                    class="input-field"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Assistenzarzt Preis (in Cent)
                  </label>
                  <input
                    v-model.number="courseForm.prices.assistenzarzt"
                    type="number"
                    required
                    min="0"
                    class="input-field"
                  />
                </div>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  v-model="courseForm.status"
                  required
                  class="input-field"
                >
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                </select>
              </div>
            </div>
            
            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="showCreateModal = false"
                class="btn-outline"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                class="btn-primary"
              >
                {{ editingCourse ? 'Aktualisieren' : 'Erstellen' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from '@/components/Layout/AdminLayout.vue'
import { Plus } from 'lucide-vue-next'
import api from '@/utils/axios'
import CourseTable from '@/components/CourseTable.vue'

export default {
  name: 'CourseList',
  components: {
    AdminLayout,
    Plus,
    CourseTable
  },
  setup() {
    const router = useRouter()
    const courses = ref([])
    const showCreateModal = ref(false)
    const editingCourse = ref(null)
    
    const courseForm = reactive({
      id: '',
      title: '',
      startDate: '',
      endDate: '',
      city: '',
      totalSpots: '',
      soldSpots: '',
      prices: {
        standard: '',
        assistenzarzt: ''
      },
      status: 'active'
    })

    const fetchCourses = async () => {
      try {
        const response = await api.get('/courses')
        courses.value = response.data
      } catch (error) {
        console.error('Error fetching courses:', error)
        // Show error state instead of mock data
        courses.value = []
      }
    }

    const viewCourseDetails = (course) => {
      // Navigate to course detail page
      router.push(`/herzbeben/courses/${course.id}`)
    }

    const editCourse = (course) => {
      editingCourse.value = course
      Object.assign(courseForm, course)
      showCreateModal.value = true
    }

    const saveCourse = async () => {
      try {
        if (editingCourse.value) {
          await api.put(`/courses/${editingCourse.value.id}`, courseForm)
        } else {
          await api.post('/courses', courseForm)
        }
        
        showCreateModal.value = false
        editingCourse.value = null
        Object.assign(courseForm, {
          id: '',
          title: '',
          startDate: '',
          endDate: '',
          city: '',
          totalSpots: '',
          soldSpots: '',
          prices: {
            standard: '',
            assistenzarzt: ''
          },
          status: 'active'
        })
        
        await fetchCourses()
      } catch (error) {
        console.error('Error saving course:', error)
      }
    }

    onMounted(() => {
      fetchCourses()
    })

    return {
      courses,
      showCreateModal,
      editingCourse,
      courseForm,
      viewCourseDetails,
      editCourse,
      saveCourse
    }
  }
}
</script> 